import { useState, useEffect, useRef, useCallback } from 'react'
import { useDisableBodyScroll } from '@/hooks/utils/useDisableBodyScroll'

/** Marks a control that opens the mobile menu but lives outside the hamburger. */
export const MENU_TRIGGER_ATTRIBUTE = 'data-menu-trigger'

export const useMobileMenu = () => {
  const [isActive, setIsActive] = useState(false)
  const menuRef = useRef<HTMLUListElement>(null)
  const hamburgerRef = useRef<HTMLDivElement>(null)

  useDisableBodyScroll({ enabled: isActive })

  const close = useCallback(() => {
    setIsActive(false)
  }, [])

  const toggle = useCallback(() => {
    setIsActive(prev => !prev)
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    if (!isActive) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Anything marked as a trigger toggles the menu on `click`. Treating it as
      // "outside" would close on `mousedown` and let the click re-open it, so
      // the control could never close what it opened.
      const isTrigger = target instanceof Element && !!target.closest(`[${MENU_TRIGGER_ATTRIBUTE}]`)

      if (
        isTrigger ||
        menuRef.current?.contains(target) ||
        hamburgerRef.current?.contains(target)
      ) {
        return
      }

      close()
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isActive, close])

  return {
    isActive,
    toggle,
    close,
    menuRef,
    hamburgerRef
  }
}
